import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../AuthProvider";
import { useI18n } from "../../i18n";

type IndexStatus = {
  displayStatus?: string;
  ragReady?: boolean;
  confidentialityTier?: string | null;
};

type Props = {
  sourceType: string;
  sourceEntityType: string;
  sourceEntityId: string;
};

const BADGE_CLASS: Record<string, string> = {
  NOT_INDEXED: "border-slate-200 bg-slate-50 text-slate-600",
  INDEXED_LOCALLY: "border-blue-200 bg-blue-50 text-blue-800",
  LOCAL_ONLY: "border-indigo-200 bg-indigo-50 text-indigo-900",
  RESTRICTED: "border-amber-200 bg-amber-50 text-amber-900",
  NO_EXTERNAL_AI: "border-slate-300 bg-slate-100 text-slate-700",
  RAG_READY: "border-emerald-200 bg-emerald-50 text-emerald-900",
  FAILED: "border-red-200 bg-red-50 text-red-800",
};

export default function GovernanceDocumentIndexStatusBadge({
  sourceType,
  sourceEntityType,
  sourceEntityId,
}: Props) {
  const { token } = useAuth();
  const { t } = useI18n();
  const [status, setStatus] = useState<IndexStatus | null>(null);

  useEffect(() => {
    if (!token || !sourceEntityId) return;
    const params = new URLSearchParams({
      sourceType,
      sourceEntityType,
      sourceEntityId,
    });
    fetch(`${API_ENDPOINTS.AI_GOVERNANCE_DOCUMENT_INDEX_STATUS}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((json) => {
        if (json?.data) setStatus(json.data as IndexStatus);
      })
      .catch(() => setStatus({ displayStatus: "NOT_INDEXED" }));
  }, [token, sourceType, sourceEntityType, sourceEntityId]);

  const key = status?.displayStatus || "NOT_INDEXED";
  const label = t(`complianceCenterUi.govDocumentIndex.${key}`, key.replace(/_/g, " "));
  const className = BADGE_CLASS[key] || BADGE_CLASS.NOT_INDEXED;

  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${className}`}
      title={t("complianceCenterUi.govDocumentIndex.tooltip", "AI document index status for this organization")}
    >
      {label}
    </span>
  );
}
